import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, ChevronRight, ChevronLeft, User, ShieldCheck, LogOut } from 'lucide-react';
import { db } from '../firebase';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';

export default function SideDrawer({ isOpen, onClose }) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSubMenu, setActiveSubMenu] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  
  const auth = getAuth();

  // Track Firebase Auth State & Check if User is Admin
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        try {
          // Check role from Firestore 'users' collection (or adjust according to your DB structure)
          const userDocRef = doc(db, 'users', user.uid);
          const userDoc = await getDoc(userDocRef);
          if (userDoc.exists() && userDoc.data().role === 'admin') {
            setIsAdmin(true);
          } else {
            setIsAdmin(false);
          }
        } catch (error) {
          console.error("Error fetching user role:", error);
          setIsAdmin(false);
        }
      } else {
        setIsAdmin(false);
      }
    });
    return () => unsubscribe();
  }, [auth]);

  // Fetch dynamic categories from Firebase Firestore
  useEffect(() => {
    const fetchDynamicCategories = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'products'));
        const productsList = querySnapshot.docs.map(doc => doc.data());
        
        const uniqueCategories = [...new Set(productsList.map(item => item.category))];
        setCategories(uniqueCategories);
      } catch (error) {
        console.error("Error fetching dynamic categories:", error);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchDynamicCategories();
      setActiveSubMenu(null);
    }
  }, [isOpen]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      onClose();
    } catch (error) {
      console.error("Error signing out: ", error);
    }
  };

  if (!isOpen) return null;

  const displayName = currentUser 
    ? (currentUser.displayName || currentUser.email.split('@')[0]) 
    : 'sign in';

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop overlay */}
      <div 
        className="absolute inset-0 bg-black/70 transition-opacity" 
        onClick={onClose}
      ></div>

      {/* Sliding Drawer Container */}
      <div className="absolute inset-y-0 left-0 max-w-xs w-full bg-white shadow-2xl flex flex-col transform transition-transform duration-300 ease-in-out overflow-hidden">
        
        {/* Drawer Header with Dynamic User & Admin Badge */}
        <div className="bg-[#232f3e] text-white px-6 py-4 flex items-center justify-between shrink-0">
          <div className="flex items-center space-x-2 overflow-hidden">
            <div className="h-8 w-8 bg-gray-600 rounded-full flex items-center justify-center font-bold shrink-0">
              <User className="h-5 w-5 text-white" />
            </div>
            <div className="flex flex-col truncate">
              <span className="text-[10px] text-gray-300">
                {currentUser ? (isAdmin ? 'Hello, Admin' : 'Hello, Customer') : 'Hello,'}
              </span>
              <span className="font-bold text-sm truncate capitalize">{displayName}</span>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="text-gray-300 hover:text-white p-1 rounded-full hover:bg-gray-700 transition shrink-0"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Drawer Body Container */}
        <div className="relative flex-1 overflow-hidden">
          
          {/* MAIN MENU VIEW */}
          <div className={`absolute inset-0 w-full h-full overflow-y-auto transition-transform duration-300 ease-in-out ${activeSubMenu ? '-translate-x-full' : 'translate-x-0'}`}>
            <div className="divide-y divide-gray-200 text-xs text-gray-800">
              
              {/* Admin Quick Panel Link (Only visible if logged-in user is Admin) */}
              {currentUser && isAdmin && (
                <div className="py-3 px-6 bg-emerald-50">
                  <Link 
                    to="/admin" 
                    onClick={onClose}
                    className="flex items-center space-x-2 font-bold text-emerald-800 hover:text-emerald-900 text-sm"
                  >
                    <ShieldCheck className="h-5 w-5 text-emerald-700" />
                    <span>Admin Dashboard Panel</span>
                  </Link>
                </div>
              )}

              {/* Trending Section */}
              <div className="py-4 px-6 space-y-3">
                <h3 className="font-bold text-sm text-gray-900 tracking-wide">Trending</h3>
                <ul className="space-y-3 text-gray-700">
                  <li><Link to="/shop" onClick={onClose} className="block hover:text-amber-600">Bestsellers</Link></li>
                  <li><Link to="/shop" onClick={onClose} className="block hover:text-amber-600">New Releases</Link></li>
                </ul>
              </div>

              {/* Dynamic Shop by Category Section */}
              <div className="py-4 px-6 space-y-3">
                <h3 className="font-bold text-sm text-gray-900 tracking-wide">Shop by Category</h3>
                <ul className="space-y-3">
                  {loading ? (
                    <li className="text-gray-400 py-2">Loading dynamic categories...</li>
                  ) : categories.length > 0 ? (
                    categories.map((cat, index) => (
                      <li key={index}>
                        <button 
                          onClick={() => setActiveSubMenu(cat)}
                          className="w-full flex items-center justify-between hover:text-amber-600 group py-1 text-left"
                        >
                          <span className="font-medium">{cat}</span>
                          <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-amber-600" />
                        </button>
                      </li>
                    ))
                  ) : (
                    <li><Link to="/shop" onClick={onClose} className="block text-gray-600">All Products</Link></li>
                  )}
                </ul>
              </div>

              {/* Programs & Features / Account Actions */}
              <div className="py-4 px-6 space-y-3">
                <h3 className="font-bold text-sm text-gray-900 tracking-wide">Help & Settings</h3>
                <ul className="space-y-3 text-gray-700">
                  <li><Link to="/my-orders" onClick={onClose} className="block hover:text-amber-600">Your Orders</Link></li>
                  {currentUser ? (
                    <li>
                      <button onClick={handleLogout} className="flex items-center space-x-1 text-rose-600 font-bold hover:underline">
                        <LogOut className="h-4 w-4" />
                        <span>Sign Out</span>
                      </button>
                    </li>
                  ) : (
                    <li><Link to="/login" onClick={onClose} className="block font-bold text-emerald-700">Sign In / Register</Link></li>
                  )}
                </ul>
              </div>

            </div>
          </div>

          {/* SUB-MENU VIEW */}
          <div className={`absolute inset-0 w-full h-full bg-white overflow-y-auto transition-transform duration-300 ease-in-out ${activeSubMenu ? 'translate-x-0' : 'translate-x-full'}`}>
            {activeSubMenu && (
              <div className="text-xs text-gray-800">
                <button 
                  onClick={() => setActiveSubMenu(null)}
                  className="w-full bg-gray-100 hover:bg-gray-200 text-gray-900 px-6 py-3 flex items-center space-x-2 font-bold border-b border-gray-300 transition"
                >
                  <ChevronLeft className="h-5 w-5 text-gray-700" />
                  <span>MAIN MENU</span>
                </button>

                <div className="py-4 px-6 space-y-4">
                  <h3 className="font-bold text-base text-gray-900 border-b pb-2">{activeSubMenu}</h3>
                  <ul className="space-y-3 text-gray-700">
                    <li>
                      <Link 
                        to={`/shop?category=${encodeURIComponent(activeSubMenu)}`} 
                        onClick={onClose}
                        className="block font-bold text-emerald-700 hover:underline"
                      >
                        Browse all items in {activeSubMenu}
                      </Link>
                    </li>
                    <li><Link to={`/shop?category=${encodeURIComponent(activeSubMenu)}`} onClick={onClose} className="block hover:text-amber-600">Featured & Popular Products</Link></li>
                  </ul>
                </div>
              </div>
            )}
          </div>

        </div>

      </div>
    </div>
  );
}